import {useState, useEffect, useMemo, useRef} from "react"
import {
    Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, TextField, IconButton, Chip, Avatar, TablePagination, MenuItem, Select,
    FormControl, InputLabel, Button, Dialog, DialogTitle, DialogContent, DialogActions,
    InputAdornment, Tooltip, Alert,
} from "@mui/material"
import {
    Search as SearchIcon, Edit as EditIcon, Delete as DeleteIcon,
    PersonAdd as PersonAddIcon, Refresh as RefreshIcon,
} from "@mui/icons-material"
import {useTranslation} from "react-i18next"
import axiosInstance from "../../config/axios-config";

const USER_ROLES = ["SYSTEM", "ADMIN", "OPERATOR", "COURIER"]
const emptyUser = () => ({name: "", surname: "", email: "", role: "OPERATOR", tenantId: ""})

export default function TabAdminUsers({tabValue}) {
    const {t} = useTranslation()
    const [users, setUsers] = useState([])
    const [loading, setLoading] = useState(false)
    const [errorMessage, setErrorMessage] = useState("")
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(10)
    const [searchQuery, setSearchQuery] = useState("")
    const [roleFilter, setRoleFilter] = useState("ALL")
    const filteredUsers = useMemo(() => users.filter(user => {
        const query = searchQuery.trim().toLowerCase()
        const matchesSearch = [user.name, user.surname, user.email].join(" ").toLowerCase().includes(query)
        return matchesSearch && (roleFilter === "ALL" || user.role === roleFilter)
    }), [users, searchQuery, roleFilter])
    const totalElements = filteredUsers.length
    const visiblePage = Math.min(page, Math.max(0, Math.ceil(totalElements / rowsPerPage) - 1))
    const visibleUsers = filteredUsers.slice(visiblePage * rowsPerPage, (visiblePage + 1) * rowsPerPage)
    const [openDialog, setOpenDialog] = useState(false)
    const [selectedUser, setSelectedUser] = useState(null)
    const [openCreateDialog, setOpenCreateDialog] = useState(false)
    const [newUser, setNewUser] = useState(emptyUser)
    const [tenants, setTenants] = useState([])
    const [tenantsLoading, setTenantsLoading] = useState(false)
    const [creating, setCreating] = useState(false)
    const createPending = useRef(false)

    useEffect(() => {
        if (!openCreateDialog) return
        const controller = new AbortController()
        setTenantsLoading(true)
        setTenants([])
        axiosInstance.get("/api/v1/tenants", {signal: controller.signal})
            .then(response => { if (!controller.signal.aborted) setTenants(response.data || []) })
            .catch(error => {
                if (!controller.signal.aborted) setErrorMessage(error.response?.data?.message || t("AdminRequestFailed"))
            })
            .finally(() => { if (!controller.signal.aborted) setTenantsLoading(false) })
        return () => controller.abort()
    }, [openCreateDialog, t])

    const fetchUsers = async () => {
        setLoading(true)
        setErrorMessage("")
        try {
            const response = await axiosInstance.get(`/api/v1/users`)
            setUsers(response.data || [])

        } catch (error) {
            console.error("Error fetching users:", error)
            setErrorMessage(error.response?.data?.message || t("AdminRequestFailed"))
        }
        setLoading(false)
    }

    useEffect(() => {
        if (tabValue === 0) fetchUsers()
    }, [tabValue])

    useEffect(() => {
        setPage(0)
    }, [searchQuery, roleFilter])

    const handleSearch = () => {
        setPage(0)
    }

    const handleDeleteUser = async (userId) => {
        if (!window.confirm(t("ConfirmDeleteUser") || "Are you sure you want to delete this user?")) return
        try {
            await axiosInstance.delete(`/api/v1/users/${userId}`)
            fetchUsers()
        } catch (error) {
            console.error("Error deleting user:", error)
            setErrorMessage(error.response?.data?.message || t("AdminRequestFailed"))
        }
    }

    const handleCreateUser = async () => {
        if (createPending.current || tenantsLoading || !tenants.some(tenant => tenant.id === newUser.tenantId)
            || !USER_ROLES.includes(newUser.role)) return
        createPending.current = true
        setCreating(true)
        setErrorMessage("")
        try {
            await axiosInstance.post("/api/v1/users", newUser)
            setOpenCreateDialog(false)
            setNewUser(emptyUser())
            fetchUsers()
        } catch (error) {
            console.error("Error creating user:", error)
            setErrorMessage(error.response?.data?.message || t("AdminRequestFailed"))
        } finally {
            createPending.current = false
            setCreating(false)
        }
    }

    const getRoleColor = (role) => {
        switch (role) {
            case "SYSTEM":
            case "ADMIN":
                return "error"
            case "OPERATOR":
                return "primary"
            case "COURIER":
                return "success"
            default:
                return "default"
        }
    }

    if (tabValue !== 0) return null

    return (
        <Box>
            {errorMessage && <Alert severity="error" sx={{mb: 2}}>{errorMessage}</Alert>}
            {/* Filters */}
            <Box sx={{display: "flex", gap: 2, mb: 3, flexWrap: "wrap", alignItems: "center"}}>
                <TextField
                    size="small"
                    placeholder={t("Search") || "Search users..."}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon/>
                            </InputAdornment>
                        ),
                    }}
                    sx={{minWidth: 250}}
                />
                <FormControl size="small" sx={{minWidth: 140}}>
                    <InputLabel>{t("Role") || "Role"}</InputLabel>
                    <Select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}
                            label={t("Role") || "Role"} variant={"outlined"}>
                        <MenuItem value="ALL">{t("All") || "All"}</MenuItem>
                        <MenuItem value="SYSTEM">{t("System") || "System"}</MenuItem>
                        <MenuItem value="ADMIN">{t("Admin") || "Admin"}</MenuItem>
                        <MenuItem value="OPERATOR">{t("Operator") || "Operator"}</MenuItem>
                        <MenuItem value="COURIER">{t("Courier") || "Courier"}</MenuItem>
                    </Select>
                </FormControl>
                <Box sx={{flex: 1}}/>
                <Button variant="contained" startIcon={<PersonAddIcon/>} onClick={() => {
                    setErrorMessage("")
                    setOpenCreateDialog(true)
                }}>
                    {t("AddUser") || "Add User"}
                </Button>
                <Tooltip title={t("Refresh") || "Refresh"}>
                    <IconButton onClick={fetchUsers}>
                        <RefreshIcon/>
                    </IconButton>
                </Tooltip>
            </Box>

            {/* Users Table */}
            <TableContainer component={Paper} elevation={1}>
                <Table>
                    <TableHead>
                        <TableRow sx={{bgcolor: "grey.50"}}>
                            <TableCell>{t("User") || "User"}</TableCell>
                            <TableCell>{t("Email") || "Email"}</TableCell>
                            <TableCell>{t("Role") || "Role"}</TableCell>
                            <TableCell>{t("CreatedAt") || "Created At"}</TableCell>
                            <TableCell align="center">{t("Actions") || "Actions"}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={5} align="center" sx={{py: 4}}>
                                    <Typography color="text.secondary">{t("Loading") || "Loading..."}</Typography>
                                </TableCell>
                            </TableRow>
                        ) : totalElements === 0 ? (
                            <TableRow>
                                <TableCell colSpan={5} align="center" sx={{py: 4}}>
                                    <Typography
                                        color="text.secondary">{t("NoUsersFound") || "No users found"}</Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            visibleUsers.map((user) => (
                                <TableRow key={user.id} hover>
                                    <TableCell>
                                        <Box sx={{display: "flex", alignItems: "center", gap: 1.5}}>
                                            <Avatar sx={{
                                                bgcolor: "primary.main",
                                                width: 36,
                                                height: 36,
                                                fontSize: "0.9rem"
                                            }}>
                                                {user.name?.charAt(0)}{user.surname?.charAt(0)}
                                            </Avatar>
                                            <Typography variant="body2" fontWeight={500}>
                                                {user.name} {user.surname}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>{user.email}</TableCell>
                                    <TableCell>
                                        <Chip label={user.role} color={getRoleColor(user.role)} size="small"/>
                                    </TableCell>
                                    <TableCell>
                                        {user.createdDate ? new Date(user.createdDate).toLocaleDateString() : "-"}
                                    </TableCell>
                                    <TableCell align="center">
                                        <Tooltip title={t("Edit") || "Edit"}>
                                            <IconButton size="small" onClick={() => {
                                                setSelectedUser(user);
                                                setOpenDialog(true)
                                            }}>
                                                <EditIcon fontSize="small"/>
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title={t("Delete") || "Delete"}>
                                            <IconButton size="small" color="error"
                                                        onClick={() => handleDeleteUser(user.id)}>
                                                <DeleteIcon fontSize="small"/>
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    component="div"
                    count={totalElements}
                    page={visiblePage}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0)
                    }}
                    labelRowsPerPage={t("RowsPerPage") || "Rows per page"}
                />
            </TableContainer>

            {/* Create User Dialog */}
            <Dialog open={openCreateDialog} onClose={() => { if (!creating) setOpenCreateDialog(false) }} maxWidth="sm" fullWidth>
                <DialogTitle>{t("AddUser") || "Add New User"}</DialogTitle>
                <DialogContent>
                    {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
                    <Box component="form" id="admin-create-user" onSubmit={event => {
                        event.preventDefault()
                        handleCreateUser()
                    }} sx={{display: "flex", flexDirection: "column", gap: 2, mt: 1}}>
                        <TextField label={t("Name") || "Name"} value={newUser.name}
                                   onChange={(e) => setNewUser({...newUser, name: e.target.value})} required disabled={creating} fullWidth/>
                        <TextField label={t("Surname") || "Surname"} value={newUser.surname}
                                   onChange={(e) => setNewUser({...newUser, surname: e.target.value})} required disabled={creating} fullWidth/>
                        <TextField label={t("Email") || "Email"} type="email" value={newUser.email}
                                   onChange={(e) => setNewUser({...newUser, email: e.target.value})} required disabled={creating} fullWidth/>
                        <FormControl required disabled={tenantsLoading || creating} fullWidth>
                            <InputLabel id="create-user-tenant-label">{t("Organization")}</InputLabel>
                            <Select labelId="create-user-tenant-label" value={newUser.tenantId} label={t("Organization")}
                                    onChange={event => setNewUser({...newUser, tenantId: event.target.value})}>
                                {tenants.map(tenant => <MenuItem key={tenant.id} value={tenant.id}>
                                    {tenant.tenantNameEn || tenant.tenantNameHy || tenant.tenantNameRu || tenant.id}
                                </MenuItem>)}
                            </Select>
                        </FormControl>
                        <FormControl disabled={creating} fullWidth>
                            <InputLabel>{t("Role") || "Role"}</InputLabel>
                            <Select value={newUser.role}
                                    onChange={(e) => setNewUser({...newUser, role: e.target.value})}
                                    label={t("Role") || "Role"} variant={"filled"}>
                                {USER_ROLES.map(role => <MenuItem key={role} value={role}>
                                    {t(role.charAt(0) + role.slice(1).toLowerCase())}
                                </MenuItem>)}
                            </Select>
                        </FormControl>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button disabled={creating} onClick={() => setOpenCreateDialog(false)}>{t("Cancel") || "Cancel"}</Button>
                    <Button variant="contained" type="submit" form="admin-create-user"
                            disabled={creating || tenantsLoading || !newUser.tenantId || !tenants.length}>
                        {t("Create") || "Create"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Edit User Dialog */}
            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{t("Edit") || "Edit User"}</DialogTitle>
                <DialogContent>
                    {selectedUser && (
                        <Box sx={{display: "flex", flexDirection: "column", gap: 2, mt: 1}}>
                            <TextField label={t("Name") || "Name"} value={selectedUser.name || ""}
                                       onChange={(e) => setSelectedUser({...selectedUser, name: e.target.value})}
                                       fullWidth/>
                            <TextField label={t("Surname") || "Surname"} value={selectedUser.surname || ""}
                                       onChange={(e) => setSelectedUser({...selectedUser, surname: e.target.value})}
                                       fullWidth/>
                            <TextField label={t("Email") || "Email"} value={selectedUser.email || ""} disabled
                                       fullWidth/>
                            <FormControl fullWidth>
                                <InputLabel>{t("Role") || "Role"}</InputLabel>
                                <Select value={selectedUser.role || ""}
                                        onChange={(e) => setSelectedUser({...selectedUser, role: e.target.value})}
                                        label={t("Role") || "Role"} variant={"filled"}>
                                    {USER_ROLES.map(role => <MenuItem key={role} value={role}>
                                        {t(role.charAt(0) + role.slice(1).toLowerCase())}
                                    </MenuItem>)}
                                </Select>
                            </FormControl>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDialog(false)}>{t("Cancel") || "Cancel"}</Button>
                    <Button variant="contained" onClick={async () => {
                        try {
                            await axiosInstance.put(`/api/v1/users/${selectedUser.id}`, selectedUser)
                            setOpenDialog(false)
                            fetchUsers()
                        } catch (error) {
                            console.error("Error updating user:", error)
                            setErrorMessage(error.response?.data?.message || t("AdminRequestFailed"))
                        }
                    }}>{t("Save") || "Save"}</Button>
                </DialogActions>
            </Dialog>
        </Box>
    )
}
